import view_history_icon from "src/assets/images/view_history_icon.png";
import restore_icon from "src/assets/images/restore_icon.png";
import apply_changes_icon from "src/assets/images/apply_changes_icon.png";
import type { AsyncStatus } from "./types";
import { ConfigurationStatusIndicator } from "../general-configuration/components/configuration-status.component";

export type ToolbarActionVariant = "default" | "primary" | "danger";

export type ToolbarAction = {
  label: string;
  onClick: () => void;
  variant?: ToolbarActionVariant;
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
};

type ConfigurationToolbarProps = {
  title: string;
  subtitle?: string;
  actions?: ToolbarAction[];
  status?: AsyncStatus;
  statusPendingLabel?: string;
  showViewHistory?: boolean;
  showRestore?: boolean;
  showApplyChanges?: boolean;
  applyingChangesACKStatus?: AsyncStatus;
  applyChangesCountdown?: number;
  onViewHistory?: () => void;
  onRestore?: () => void;
  onApplyChanges?: () => void;
};

export function ConfigurationToolbar({
  title,
  subtitle,
  actions,
  status,
  statusPendingLabel = "Pending",
  showViewHistory = false,
  showRestore = false,
  showApplyChanges = false,
  applyingChangesACKStatus,
  applyChangesCountdown,
  onViewHistory,
  onRestore,
  onApplyChanges,
}: ConfigurationToolbarProps) {
  const renderActionButton = (action: ToolbarAction) => {
    const className = action.variant === "primary"
      ? "configuration-button configuration-button--primary"
      : action.variant === "danger"
        ? "configuration-button configuration-button--danger"
        : "configuration-button";

    return (
      <button
        key={action.label}
        type="button"
        className={className}
        onClick={action.onClick}
        disabled={action.disabled || action.loading}
      >
        {action.icon && (
          <img src={action.icon} alt={`${action.label} icon`} />
        )}
        {action.loading && action.loadingLabel ? action.loadingLabel : action.label}
      </button>
    );
  };

  return (
    <div className="configuration-toolbar">
      <div>
        <h2 className="configuration-page-title">{title}</h2>
        {subtitle && (
          <p className="configuration-page-subtitle">{subtitle}</p>
        )}
      </div>

      <div className="configuration-toolbar__actions">
        {actions?.map(renderActionButton)}

        {showViewHistory && (
          <button
            type="button"
            className="configuration-button"
            onClick={onViewHistory}
          >
            <img src={view_history_icon} alt="view history button icon" height={18} width={18} />
            View History
          </button>
        )}

        {showRestore && (
          <button
            type="button"
            className="configuration-button"
            onClick={onRestore}
          >
            <img src={restore_icon} alt="Restore button icon" height={20} width={20} />
            Restore
          </button>
        )}

        {showApplyChanges && applyingChangesACKStatus != null && (
          <>
            <button
              type="button"
              className="configuration-button configuration-button--primary"
              onClick={onApplyChanges}
              disabled={applyingChangesACKStatus === "pending"}
            >
              <img src={apply_changes_icon} alt="Apply changes icon" />
              {applyingChangesACKStatus === "pending" ? "Applying..." : "Apply Changes"}
            </button>
            <ConfigurationStatusIndicator
              status={applyingChangesACKStatus}
              pendingLabel={statusPendingLabel}
              countdown={applyChangesCountdown}
            />
          </>
        )}

        {status != null && !showApplyChanges && (
          <ConfigurationStatusIndicator status={status} pendingLabel={statusPendingLabel} />
        )}
      </div>
    </div>
  );
}
